/**
 * HTTP client layer: axios instances, auth interceptors, token refresh and the
 * request cache.
 *
 * The refresh/queue state below is shared mutable state, so it is kept in this
 * one module. Endpoint modules import { api, handleRequest, ... } from here.
 */
import axios from 'axios';
import { config } from '../config/env';

if (!config.apiUrl) {
  throw new Error('API URL is not configured. Please set REACT_APP_API_URL environment variable.');
}

const API_BASE_URL = config.apiUrl;
const REPORTS_SERVER_URL = config.reportsServerUrl || config.apiUrl;

// Log reports server URL for debugging
console.log('Reports Server URL:', REPORTS_SERVER_URL);

// Helper function to get the JWT token from localStorage
const getToken = () => localStorage.getItem('token');

// Event name for organization ID changes
export const ORG_ID_CHANGE_EVENT = 'orgIdChange';

// Event for authentication status
export const AUTH_STATUS_EVENT = 'authStatusChange';

// Function to get current organization ID from localStorage
export const getCurrentOrgId = () => {
  return localStorage.getItem('orgId');
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-Organization-ID': getCurrentOrgId()
  },
});

// Add a queue for requests that failed due to 401
let isRefreshing = false;
let failedQueue = [];
let refreshTokenPromise = null;

const processFailedQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Helper to clear all auth data
const clearAuthData = () => {
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userRoles');
  localStorage.removeItem('displayName');
  localStorage.removeItem('orgId');
};

// Helper to store auth data
const storeAuthData = (data) => {
  const { token, refreshToken, uid, roles, displayName, orgId } = data;
  
  // Validate required tokens
  if (!token || !refreshToken) {
    throw new Error('Invalid authentication data: missing required tokens');
  }

  // Store tokens
  localStorage.setItem('token', token);
  localStorage.setItem('refreshToken', refreshToken);
  
  // Store user data
  localStorage.setItem('userId', uid);
  if (roles) localStorage.setItem('userRoles', JSON.stringify(roles));
  if (displayName) localStorage.setItem('displayName', displayName);
  if (orgId) localStorage.setItem('orgId', orgId);

  // Log success (without sensitive data)
  console.log('Authentication data stored successfully');
};

// Function to refresh token with proper synchronization
const refreshTokenInternal = async () => {
  const refreshTokenValue = localStorage.getItem('refreshToken');
  if (!refreshTokenValue) {
    throw new Error('No refresh token available');
  }

  try {
    console.log('Explicitly refreshing token...');
    const response = await axios.post(`${API_BASE_URL}/auth/refresh-token`, {
      refreshToken: refreshTokenValue
    });

    console.log('Refresh token response:', response.data);

    const { token, refreshToken: newRefreshToken, user } = response.data;
    
    if (!token || !newRefreshToken || !user) {
      console.error('Missing required data in response:', response.data);
      throw new Error('Invalid refresh token response');
    }

    // Store tokens
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', newRefreshToken);
    
    // Store user data
    localStorage.setItem('userId', user.uid);
    localStorage.setItem('userRoles', JSON.stringify(user.roles));
    localStorage.setItem('displayName', user.displayName);
    localStorage.setItem('email', user.email);
    if (user.orgId) localStorage.setItem('orgId', user.orgId);
    if (user.photoURL) localStorage.setItem('photoURL', user.photoURL);

    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { 
        status: 'refreshed',
        user: {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          roles: user.roles,
          orgId: user.orgId
        }
      }
    }));

    return response.data;
  } catch (error) {
    clearAuthData();

    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: {
        status: 'expired',
        message: error.response?.data?.message || 'Session expired'
      }
    }));

    throw error;
  }
};

// Synchronized token refresh function
export const refreshTokenManually = async () => {
  console.log('[Token Refresh] refreshTokenManually called, isRefreshing:', isRefreshing);
  
  if (isRefreshing) {
    console.log('[Token Refresh] Already refreshing, waiting for existing promise');
    return refreshTokenPromise;
  }

  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.error('[Token Refresh] No refresh token in localStorage');
    const error = new Error('No refresh token available - user must login again');
    error.code = 'NO_REFRESH_TOKEN';
    throw error;
  }

  try {
    isRefreshing = true;
    console.log('[Token Refresh] Starting token refresh, refreshToken present:', !!refreshToken);
    refreshTokenPromise = refreshTokenInternal();
    const refreshResult = await refreshTokenPromise;
    
    console.log('[Token Refresh] Refresh completed, result:', {
      hasResult: !!refreshResult,
      hasToken: !!(refreshResult && refreshResult.token),
      tokenLength: refreshResult?.token?.length,
      hasUser: !!refreshResult?.user
    });
    
    if (!refreshResult || !refreshResult.token) {
      console.error('[Token Refresh] Invalid refresh result:', refreshResult);
      clearAuthData();
      throw new Error('Session refresh failed: Invalid response');
    }

    // Update axios default headers
    api.defaults.headers.common['Authorization'] = `Bearer ${refreshResult.token}`;
    
    console.log('[Token Refresh] Token refresh successful, new token stored');
    
    // Dispatch success event
    window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
      detail: { 
        status: 'refreshed',
        user: {
          uid: refreshResult.user?.uid || refreshResult.uid,
          email: refreshResult.user?.email || refreshResult.email,
          displayName: refreshResult.user?.displayName || refreshResult.displayName,
          roles: refreshResult.user?.roles || refreshResult.roles,
          orgId: refreshResult.user?.orgId || refreshResult.orgId
        }
      }
    }));

    return refreshResult;
  } catch (error) {
    console.error('[Token Refresh] Token refresh failed:', {
      message: error.message,
      code: error.code,
      responseStatus: error.response?.status,
      responseData: error.response?.data
    });
    
    // Handle specific error cases
    if (error.response?.status === 401) {
      clearAuthData();
      const authError = new Error('Refresh token expired - user must login again');
      authError.code = 'REFRESH_TOKEN_EXPIRED';
      throw authError;
    }
    
    // Clear auth data on any refresh failure
    clearAuthData();
    throw error;
  } finally {
    isRefreshing = false;
    refreshTokenPromise = null;
    console.log('[Token Refresh] Refresh process completed, isRefreshing reset to false');
  }
};

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle network errors
    if (!error.response) {
      window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
        detail: {
          status: 'network_error',
          message: 'Network error. Please check your connection.'
        }
      }));
      return Promise.reject(error);
    }

    const status = error.response.status;

    // Handle 401 errors
    if (status === 401 && !originalRequest._retry) {
      const isBulkUpdate = originalRequest.url && originalRequest.url.includes('bulk-update');
      
      // Log the 401 error for debugging
      if (isBulkUpdate) {
        console.log('[Response Interceptor] 401 error on bulk update:', {
          url: originalRequest.url,
          hadAuthHeader: !!originalRequest.headers?.Authorization,
          authHeaderPrefix: originalRequest.headers?.Authorization?.substring(0, 20),
          refreshTokenPresent: !!localStorage.getItem('refreshToken'),
          tokenPresent: !!localStorage.getItem('token')
        });
      }
      
      // Skip token refresh for bulk update requests that might be timing out
      // These will be retried with the same token if it's just a timeout
      if (isBulkUpdate) {
        // For bulk updates, check if it's a timeout vs actual auth error
        if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
          // Timeout error, don't invalidate session - just retry once
          if (!originalRequest._timeoutRetry) {
            originalRequest._timeoutRetry = true;
            return api(originalRequest);
          }
        }
      }

      if (isRefreshing) {
        // Queue this request while refreshing
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return api(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        console.log('[Response Interceptor] Attempting to refresh token for 401 error');
        const refreshResult = await refreshTokenManually();
        console.log('[Response Interceptor] Token refresh completed, result:', {
          hasResult: !!refreshResult,
          hasToken: !!(refreshResult && refreshResult.token),
          resultKeys: refreshResult ? Object.keys(refreshResult) : []
        });
        
        const newToken = refreshResult && refreshResult.token;
        
        if (!newToken) {
          console.error('[Response Interceptor] No token found in refreshResult:', {
            refreshResult,
            refreshResultType: typeof refreshResult,
            refreshResultKeys: refreshResult ? Object.keys(refreshResult) : [],
            refreshResultToken: refreshResult?.token,
            refreshResultUser: refreshResult?.user
          });
          if (isBulkUpdate) {
            console.error('[Response Interceptor] Bulk update failed: token refresh returned no token');
          }
          clearAuthData();
          // Optionally, force logout or redirect to login
          return Promise.reject(new Error('Session refresh failed: No token returned'));
        }
        
        // Update token in localStorage
        localStorage.setItem('token', newToken);
        
        // Update headers for future requests
        api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
        
        // Process queued requests
        processFailedQueue(null, newToken);
        
        // Retry original request with new token
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        if (isBulkUpdate) {
          console.log('[Response Interceptor] Retrying bulk update with refreshed token');
        }
        
        return api(originalRequest);
      } catch (refreshError) {
        // Process queued requests with error
        processFailedQueue(refreshError, null);
        
        if (isBulkUpdate) {
          console.error('[Response Interceptor] Token refresh failed for bulk update:', refreshError);
        }
        
        // Only clear auth data if it's a real auth error, not a timeout
        if (refreshError.response?.status === 401 || refreshError.message?.includes('expired')) {
          clearAuthData();
          window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
            detail: {
              status: 'refresh_failed',
              message: refreshError.message || 'Session expired'
            }
          }));
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle 403 errors
    if (status === 403) {
      window.dispatchEvent(new CustomEvent(AUTH_STATUS_EVENT, {
        detail: {
          status: 'permission_denied',
          message: error.response?.data?.message || 'Permission denied'
        }
      }));
    }

    return Promise.reject(error);
  }
);

// Add request interceptor for token validation
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const orgId = localStorage.getItem('orgId') || '1066';
    
    // Log bulk update request details
    const isBulkUpdate = config.url?.includes('bulk-update');
    if (isBulkUpdate) {
      console.log('[Request Interceptor] Bulk update request detected:', {
        url: config.url,
        tokenPresent: !!token,
        tokenLength: token?.length,
        orgId,
        method: config.method,
        existingHeaders: Object.keys(config.headers || {})
      });
    }

    // Always ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    if (token) {
      // Explicitly set the Authorization header (axios normalizes header names)
      // Set both capitalized and lowercase to ensure compatibility
      config.headers.Authorization = `Bearer ${token}`;
      config.headers.authorization = `Bearer ${token}`;
      if (isBulkUpdate) {
        console.log('[Request Interceptor] Authorization header set for bulk update:', {
          headerValue: `Bearer ${token.substring(0, 20)}...`,
          fullHeaderLength: config.headers.Authorization?.length,
          bothHeadersSet: !!(config.headers.Authorization && config.headers.authorization)
        });
      }
    } else {
      // Remove Authorization header if token is missing
      delete config.headers.Authorization;
      delete config.headers.authorization;
      if (isBulkUpdate) {
        console.warn('[Request Interceptor] WARNING: No token found for bulk update request!');
      }
    }
    
    // Always set organization ID - ensure it's set on the headers object
    config.headers['X-Organization-ID'] = orgId;
    
    // Log bulk update request details
    if (isBulkUpdate) {
      console.log('[Request Interceptor] Final headers for bulk update:', {
        hasAuthorization: !!config.headers.Authorization,
        authorizationPrefix: config.headers.Authorization?.substring(0, 30) + '...',
        authorizationFullLength: config.headers.Authorization?.length,
        organizationId: config.headers['X-Organization-ID'],
        contentType: config.headers['Content-Type'],
        allHeaderKeys: Object.keys(config.headers)
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Helper function to get headers with auth token and org ID
const getHeaders = () => {
  const token = localStorage.getItem('token');
  const orgId = localStorage.getItem('orgId') || '1066';
  const headers = {
    'Content-Type': 'application/json',
    'X-Organization-ID': orgId
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

// Cache for API responses
const apiCache = new Map();
const pendingApiRequests = new Map();

// Cache timeout in milliseconds (5 minutes)
const CACHE_TIMEOUT = 5 * 60 * 1000;

// Helper function to create a cache key
const createCacheKey = (endpoint, params = {}) => {
  // Include orgId in cache key if it's part of the endpoint or params
  const orgId = params.orgId || (endpoint.includes('/organization/') ? endpoint.split('/organization/')[1]?.split('/')[0] : null);
  return `${endpoint}:${orgId || ''}:${JSON.stringify(params)}`;
};

// Helper function to handle API requests with caching and deduplication
const handleRequest = async (endpoint, requestFn) => {
  const cacheKey = createCacheKey(endpoint);

  // Check cache first
  const cachedData = apiCache.get(cacheKey);
  if (cachedData && (Date.now() - cachedData.timestamp) < CACHE_TIMEOUT) {
    return cachedData.data;
  }

  // Check for pending requests
  if (pendingApiRequests.has(cacheKey)) {
    return pendingApiRequests.get(cacheKey);
  }

  // Create new request promise
  const requestPromise = requestFn().then(response => {
    // Cache the successful response
    apiCache.set(cacheKey, {
      data: response,
      timestamp: Date.now()
    });
    pendingApiRequests.delete(cacheKey);
    return response;
  }).catch(error => {
    pendingApiRequests.delete(cacheKey);
    throw error;
  });

  // Store the pending request
  pendingApiRequests.set(cacheKey, requestPromise);
  return requestPromise;
};

// Clear cache for specific endpoint or all cache if no endpoint provided
export const clearCache = (endpoint = null) => {
  if (endpoint) {
    const cacheKey = createCacheKey(endpoint);
    apiCache.delete(cacheKey);
  } else {
    apiCache.clear();
  }
};

// Auth endpoints

// Consumed by the endpoint modules.
export { API_BASE_URL, REPORTS_SERVER_URL, api, handleRequest };
